/**
 * Utility functions for optimizing images
 */

/**
 * Convert an image to WebP format with optimized settings
 * @param {File} file - The image file to convert
 * @param {Object} options - Conversion options
 * @param {number} options.quality - WebP quality 0-1 (default 0.8)
 * @param {number} options.maxWidth - Maximum width (default 1200)
 * @param {number} options.maxHeight - Maximum height (default 1200)
 * @returns {Promise<File>} Converted image as a File object
 */
export const convertToWebP = (file, options = {}) => {
  const { quality = 0.8, maxWidth = 1200, maxHeight = 1200 } = options;

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        // Calculate resize ratio if image exceeds maximum dimensions
        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to WebP format
        canvas.toBlob(
          (blob) => {
            // Create a new filename with .webp extension
            const originalName = file.name.split(".").slice(0, -1).join(".");
            const newFileName = `${originalName}.webp`;
            
            resolve(
              new File([blob], newFileName, {
                type: "image/webp",
                lastModified: Date.now(),
              })
            );
          },
          "image/webp",
          quality
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Process an image URL by downloading and converting to WebP
 * @param {string} imageUrl - URL of the image to process
 * @param {Object} options - Conversion options (same as convertToWebP)
 * @returns {Promise<{file: File, preview: string}>}
 */
export const processImageUrlToWebP = async (imageUrl, options = {}) => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const fileName = imageUrl.split("/").pop().split("?")[0] || `image-${Date.now()}.jpg`;
    
    const file = new File([blob], fileName, {
      type: blob.type,
    });
    
    const webpFile = await convertToWebP(file, options);
    return {
      file: webpFile,
      preview: URL.createObjectURL(webpFile),
    };
  } catch (error) {
    console.error("Error processing image URL to WebP:", error);
    throw error;
  }
};

/**
 * Check if browser supports WebP format
 * @returns {Promise<boolean>} True if WebP is supported
 */
export const isWebPSupported = () => {
  return new Promise((resolve) => {
    const webP = new Image();
    webP.onload = () => resolve(webP.height === 1);
    webP.onerror = () => resolve(false);
    webP.src = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=';
  });
};

/**
 * Lazy load images by replacing src with data-src
 * Call this function after the component is mounted
 */
export const lazyLoadImages = () => {
  const images = document.querySelectorAll('img[data-src]');
  
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
          imageObserver.unobserve(img);
        }
      });
    });
    
    images.forEach(img => imageObserver.observe(img));
  } else {
    // Fallback for browsers that don't support IntersectionObserver
    images.forEach(img => {
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    });
  }
};

/**
 * Add loading="lazy" attribute to all images in a component
 * @param {HTMLElement} container - The container element to search for images
 */
export const addLazyLoadingToImages = (container) => {
  if (!container) return;
  
  const images = container.querySelectorAll('img:not([loading])');
  images.forEach(img => {
    img.setAttribute('loading', 'lazy');
  });
};
